import { OnboardingState } from 'src/api/mutations/useGetOnboardingState';

export interface OnboardingProgress {
  hasStep1: boolean;
  hasStep2: boolean;
  hasStep3: boolean;
  hasStep4: boolean;
  completedStepsCount: number;
  nextStepNumber: number; // 1 = Experience, 2 = Client Types, 3 = Offers, 4 = Schedule
  nextHeroWizardStep: number; // 3 = Experience, 4 = Client Types, 5 = Offers, 6 = Schedule
  isFullyCompleted: boolean;
  isSkipped: boolean;
}

export function hasStep1Experience(onboarding?: OnboardingState | null): boolean {
  if (!onboarding?.step1_experience) return false;
  if (Array.isArray(onboarding.step1_experience)) {
    return onboarding.step1_experience.length > 0;
  }
  if (typeof onboarding.step1_experience === 'string') {
    return onboarding.step1_experience.trim().length > 0;
  }
  return false;
}

export function hasStep2ClientTypes(onboarding?: OnboardingState | null): boolean {
  if (!onboarding?.step2_client_types) return false;
  if (Array.isArray(onboarding.step2_client_types)) {
    return onboarding.step2_client_types.length > 0;
  }
  return false;
}

export function hasStep3Offers(onboarding?: OnboardingState | null): boolean {
  if (!onboarding?.step3_offers) return false;
  if (Array.isArray(onboarding.step3_offers)) {
    return onboarding.step3_offers.length > 0;
  }
  return false;
}

export function hasStep4Schedule(onboarding?: OnboardingState | null): boolean {
  if (!onboarding) return false;
  const isSkipped = Boolean(
    onboarding.onboardingSkipped ?? (onboarding as { skipped?: boolean }).skipped,
  );
  if (isSkipped) {
    return false;
  }

  if (onboarding.step4_schedule && typeof onboarding.step4_schedule === 'object') {
    const schedule = onboarding.step4_schedule as {
      timezone?: string;
      weekly_schedule?: unknown;
    };
    if (schedule.timezone && typeof schedule.timezone === 'string' && schedule.timezone.trim()) {
      return true;
    }
  }

  return (onboarding.lastCompletedStep ?? 0) >= 4;
}

/**
 * Computes the real step completion progress and next step to resume based on the actual
 * presence of data in the user's onboarding document.
 */
export function computeOnboardingProgress(onboarding?: OnboardingState | null): OnboardingProgress {
  if (!onboarding) {
    return {
      hasStep1: false,
      hasStep2: false,
      hasStep3: false,
      hasStep4: false,
      completedStepsCount: 0,
      nextStepNumber: 1,
      nextHeroWizardStep: 3,
      isFullyCompleted: false,
      isSkipped: false,
    };
  }

  const isSkipped = Boolean(
    onboarding.onboardingSkipped ?? (onboarding as { skipped?: boolean }).skipped,
  );

  const hasStep1 = hasStep1Experience(onboarding);
  const hasStep2 = hasStep2ClientTypes(onboarding);
  const hasStep3 = hasStep3Offers(onboarding);
  const hasStep4 = hasStep4Schedule(onboarding);

  let completedStepsCount = 0;
  if (hasStep1) completedStepsCount++;
  if (hasStep2) completedStepsCount++;
  if (hasStep3) completedStepsCount++;
  if (hasStep4) completedStepsCount++;

  let nextStepNumber = 1;
  let nextHeroWizardStep = 3;

  if (!hasStep1) {
    nextStepNumber = 1;
    nextHeroWizardStep = 3; // Experience
  } else if (!hasStep2) {
    nextStepNumber = 2;
    nextHeroWizardStep = 4; // ClientType: "What type of clients are you looking for?"
  } else if (!hasStep3) {
    nextStepNumber = 3;
    nextHeroWizardStep = 5; // Offer: "What do you offer to your clients?"
  } else if (!hasStep4) {
    nextStepNumber = 4;
    nextHeroWizardStep = 6; // Schedule: "Let's set your schedule"
  } else {
    nextStepNumber = 4;
    nextHeroWizardStep = 6;
  }

  const isFullyCompleted = !isSkipped && completedStepsCount === 4;

  return {
    hasStep1,
    hasStep2,
    hasStep3,
    hasStep4,
    completedStepsCount,
    nextStepNumber,
    nextHeroWizardStep,
    isFullyCompleted,
    isSkipped,
  };
}
